import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, SectionMargins, MapSectionContent } from "api/shop";
import {
  HeadingTagSelect,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
  PaziaTextarea,
} from "components/atoms";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

const FORM_NAMES = {
  ANIMATION: "animation",
  HEADER_TEXT: "headerText",
  HEADER_TAG: "headerTag",
  POINTS: "points",
  MAP_CENTER_LONGITUDE: "mapCenterLongitude",
  MAP_CENTER_LATITUDE: "mapCenterLatitude",
  MAP_ZOOM: "mapZoom",
} as const;

const pointSchema = yup.object({
  longitude: yup.number().required("Długość geograficzna jest wymagana"),
  latitude: yup.number().required("Szerokość geograficzna jest wymagana"),
  name: yup.string().required("Nazwa jest wymagana"),
  description: yup.string().default(""),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.HEADER_TEXT]: yup.string(),
    [FORM_NAMES.HEADER_TAG]: yup.string(),
    [FORM_NAMES.POINTS]: yup.array().of(pointSchema).min(1, "Dodaj przynajmniej jeden punkt"),
    [FORM_NAMES.MAP_CENTER_LONGITUDE]: yup.number().nullable(),
    [FORM_NAMES.MAP_CENTER_LATITUDE]: yup.number().nullable(),
    [FORM_NAMES.MAP_ZOOM]: yup.number().min(1).max(20).nullable(),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormValues {
  animation: string;
  headerText: string;
  headerTag: string;
  points: Array<{ longitude: number; latitude: number; name: string; description: string }>;
  mapCenterLongitude: number | null;
  mapCenterLatitude: number | null;
  mapZoom: number | null;
  margins: SectionMargins;
}

export interface MapSectionFormValues {
  animation: string;
  header?: { text: string; tag?: string };
  points: Array<{ longitude: number; latitude: number; name: string; description: string }>;
  mapCenter?: { longitude: number; latitude: number };
  mapZoom?: number;
  margins?: SectionMargins;
}

interface MapSectionFormProps {
  onSubmit: (data: MapSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: MapSectionContent;
  isLoading?: boolean;
}

export const MapSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: MapSectionFormProps) => {
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.HEADER_TEXT]: defaultValues?.header?.text ?? "",
      [FORM_NAMES.HEADER_TAG]: defaultValues?.header?.tag ?? "h2",
      [FORM_NAMES.POINTS]: defaultValues?.points ?? [],
      [FORM_NAMES.MAP_CENTER_LONGITUDE]: defaultValues?.mapCenter?.longitude ?? null,
      [FORM_NAMES.MAP_CENTER_LATITUDE]: defaultValues?.mapCenter?.latitude ?? null,
      [FORM_NAMES.MAP_ZOOM]: defaultValues?.mapZoom ?? null,
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control, formState } = methods;

  console.log("Form errors:", formState.errors);
  const {
    fields: pointFields,
    append: appendPoint,
    remove: removePoint,
  } = useFieldArray({
    control,
    name: FORM_NAMES.POINTS,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const formattedData: MapSectionFormValues = {
      animation: data.animation,
      header: data.headerText ? { text: data.headerText, tag: data.headerTag as any } : undefined,
      points: data.points,
      mapCenter:
        data.mapCenterLongitude != null && data.mapCenterLatitude != null
          ? { longitude: data.mapCenterLongitude, latitude: data.mapCenterLatitude }
          : undefined,
      mapZoom: data.mapZoom ?? undefined,
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addPoint = () => {
    appendPoint({ longitude: 0, latitude: 0, name: "", description: "" });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaSelect name={FORM_NAMES.ANIMATION} label="Animacja" required options={ANIMATION_OPTIONS} />

        {/* Header */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Nagłówek (opcjonalny)</h4>
          <PaziaInput name={FORM_NAMES.HEADER_TEXT} label="Tekst nagłówka" />
          <div className="mt-3">
            <HeadingTagSelect name={FORM_NAMES.HEADER_TAG} />
          </div>
        </div>

        {/* Points Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Punkty na mapie</h4>
            <button
              type="button"
              onClick={addPoint}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj punkt
            </button>
          </div>

          {pointFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak punktów. Kliknij "Dodaj punkt", aby dodać.
            </p>
          ) : (
            <div className="space-y-4">
              {pointFields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Punkt #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removePoint(index)}
                      className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      title="Usuń punkt"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                  <div className="grid gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <PaziaInput
                        name={`${FORM_NAMES.POINTS}.${index}.longitude`}
                        label="Długość geogr. (longitude)"
                        type="number"
                        step="any"
                        required
                      />
                      <PaziaInput
                        name={`${FORM_NAMES.POINTS}.${index}.latitude`}
                        label="Szerokość geogr. (latitude)"
                        type="number"
                        step="any"
                        required
                      />
                    </div>
                    <PaziaInput name={`${FORM_NAMES.POINTS}.${index}.name`} label="Nazwa" required />
                    <PaziaTextarea name={`${FORM_NAMES.POINTS}.${index}.description`} label="Opis" rows={2} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map Settings */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Ustawienia mapy (opcjonalne)</h4>
          <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
            Jeśli nie podasz centrum mapy, zostanie obliczone automatycznie na podstawie punktów.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <PaziaInput
              name={FORM_NAMES.MAP_CENTER_LONGITUDE}
              label="Centrum - długość geogr."
              type="number"
              step="any"
            />
            <PaziaInput
              name={FORM_NAMES.MAP_CENTER_LATITUDE}
              label="Centrum - szerokość geogr."
              type="number"
              step="any"
            />
            <PaziaInput name={FORM_NAMES.MAP_ZOOM} label="Zoom (1-20)" type="number" />
          </div>
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};
